'use client';

import React from 'react';

const principles = [
  {
    n: '01',
    title: 'Accountable',
    icon: '📋',
    body: 'Every agent decision is logged with reasoning, inputs, and confidence. Humans can inspect, override, and correct. Developers are responsible for their agents\' outcomes.',
  },
  {
    n: '02',
    title: 'Fair & Inclusive',
    icon: '⚖️',
    body: 'Models tested for bias across protected classes. Tenant screening audited by third parties for disparate impact. No AI can deny housing without human review.',
  },
  {
    n: '03',
    title: 'Transparent',
    icon: '🔍',
    body: 'Every AI-assisted action is labeled. Users can ask "why did you do that?" and get an explanation. Source documents for AI claims are always linked.',
  },
  {
    n: '04',
    title: 'Reliable & Safe',
    icon: '🛡',
    body: 'Hard-coded guardrails: no autonomous evictions, no autonomous rent increases above X%, no autonomous tenant rejections. Continuous drift monitoring.',
  },
  {
    n: '05',
    title: 'Privacy & Security',
    icon: '🔐',
    body: 'Tenant PII isolated per customer. No cross-customer training. SOC 2 Type II. GDPR · POPIA · CCPA · HIPAA. Data residency in EU, US, or ZA.',
  },
];

const jurisdictions = [
  { region: 'EU', regulation: 'GDPR · EU AI Act', action: 'Data residency in EU, right to explanation, bias audits' },
  { region: 'USA', regulation: 'Fair Housing Act · CCPA', action: 'Disparate impact testing, CA privacy rights' },
  { region: 'South Africa', regulation: 'POPIA', action: 'Data residency in ZA, consent management' },
  { region: 'UK', regulation: 'UK GDPR · Renters Reform Bill', action: 'Section 21 compliance agents' },
  { region: 'Australia', regulation: 'Privacy Act · Residential Tenancy Act', action: 'State-specific lease agents' },
];

export default function AIResponsible() {
  return (
    <section className="py-24 px-6 relative">
      <div className="max-w-6xl mx-auto">
        <div className="reveal-blur mb-12 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[rgba(34,197,94,0.25)] mb-5">
            <span className="w-1 h-1 rounded-full bg-[#22C55E]" />
            <span className="text-[10px] font-mono text-[#22C55E] tracking-widest uppercase">
              Responsible AI Framework
            </span>
          </div>
          <h2
            className="font-manrope font-extrabold tracking-tighter text-[#EEEEF0]"
            style={{ fontSize: 'clamp(1.75rem, 4vw, 3rem)' }}
          >
            Table stakes for
            <br />
            <span
              style={{
                background: 'linear-gradient(135deg, #22C55E, #A78BFA)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              enterprise-grade AI.
            </span>
          </h2>
        </div>

        {/* Principles */}
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 stagger-children mb-10">
          {principles.map((p) => (
            <div
              key={p.n}
              className="reveal-blur bento-card card-shine p-5 flex flex-col gap-3"
            >
              <div className="flex items-center justify-between">
                <div className="text-2xl">{p.icon}</div>
                <span className="text-[10px] font-mono text-[#5A5A6E] tracking-widest">{p.n}</span>
              </div>
              <h3 className="font-manrope font-bold text-base text-[#EEEEF0] tracking-tight">
                {p.title}
              </h3>
              <p className="text-[13px] text-[#9999A8] leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>

        {/* Jurisdictional table */}
        <div className="reveal-blur bento-card card-shine overflow-hidden">
          <div className="px-6 py-4 border-b border-[rgba(255,255,255,0.05)] bg-[rgba(124,58,237,0.04)]">
            <div className="text-[10px] font-mono text-[#A78BFA] tracking-widest uppercase">
              Jurisdictional Compliance
            </div>
            <div className="text-sm text-[#EEEEF0] font-manrope font-semibold mt-1">
              Global-ready by design
            </div>
          </div>
          <div className="grid grid-cols-12 text-[10px] font-mono text-[#5A5A6E] tracking-widest uppercase px-6 py-3 border-b border-[rgba(255,255,255,0.04)]">
            <div className="col-span-3">Region</div>
            <div className="col-span-4">Regulation</div>
            <div className="col-span-5">Propmatic action</div>
          </div>
          {jurisdictions.map((j, i) => (
            <div
              key={j.region}
              className={`grid grid-cols-12 px-6 py-4 items-center ${
                i !== jurisdictions.length - 1 ? 'border-b border-[rgba(255,255,255,0.04)]' : ''
              }`}
            >
              <div className="col-span-3 text-sm text-[#EEEEF0] font-manrope font-semibold">{j.region}</div>
              <div className="col-span-4 text-xs font-mono text-[#A78BFA]">{j.regulation}</div>
              <div className="col-span-5 text-xs text-[#9999A8]">{j.action}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
